import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr } from "@/lib/api";

const EMPTY = { current: "", next: "", confirm: "" };

const PasswordField = ({ id, label, value, onChange, error }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} className="pr-10" autoComplete="off" data-testid={`${id}-input`} />
        <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid={`${id}-toggle`}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-white">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-rose-400" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
};

function validate(v) {
  const e = {};
  if (!v.current) e.current = "Enter your current password";
  if (v.next.length < 8) e.next = "Must be at least 8 characters";
  else if (v.next === v.current) e.next = "Must be different from the current password";
  if (v.confirm !== v.next) e.confirm = "Passwords do not match";
  return e;
}

export const ChangePasswordCard = () => {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (ev) => setV({ ...v, [k]: ev.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: v.current, new_password: v.next });
      setV(EMPTY);
      toast.success("Password updated. A security notice was sent to your email.");
    } catch (err) {
      const msg = formatErr(err);
      if (/current password is incorrect/i.test(msg)) setErrors({ current: msg });
      else if (/different/i.test(msg)) setErrors({ next: msg });
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-surface space-y-4 p-6" data-testid="change-password-form" noValidate>
      <h3 className="text-xl font-bold uppercase text-white">Change password</h3>
      <PasswordField id="current-password" label="Current password" value={v.current} onChange={set("current")} error={errors.current} />
      <PasswordField id="new-password" label="New password (min 8 characters)" value={v.next} onChange={set("next")} error={errors.next} />
      <PasswordField id="confirm-password" label="Confirm new password" value={v.confirm} onChange={set("confirm")} error={errors.confirm} />
      <Button type="submit" className="rounded-full" disabled={busy} data-testid="change-password-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
      </Button>
    </form>
  );
};
